import type { FC, ReactNode } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

import { checkRevertFlag, RevertEditorSuccess } from '../EditorRevert/index.ts';

export type AutoConversionContextValue = {
	editorProperty: string;
	fabricEditorSupported: boolean | null | undefined;
	isAutoConverted: boolean | null | undefined;
	queryDataId: string | null | undefined;
};

export const AutoConversionByLineContext: React.Context<AutoConversionContextValue> =
	createContext<AutoConversionContextValue>({
		editorProperty: '',
		fabricEditorSupported: false,
		isAutoConverted: false,
		queryDataId: '',
	});

type AutoConversionContextProviderProps = {
	editorProperty: string;
	fabricEditorSupported: boolean | null | undefined;
	isAutoConverted: boolean;
	queryDataId: string | null | undefined;
	children?: ReactNode;
};

export const AutoConversionByLineContextProvider: FC<AutoConversionContextProviderProps> = ({
	editorProperty,
	fabricEditorSupported,
	isAutoConverted,
	queryDataId,
	children,
}) => {
	const isFabric = editorProperty === 'v2' || isAutoConverted;

	const autoConversionContextValue = useMemo(
		() => ({
			editorProperty,
			fabricEditorSupported,
			isAutoConverted,
			queryDataId,
		}),
		[editorProperty, fabricEditorSupported, isAutoConverted, queryDataId],
	);

	return (
		<AutoConversionByLineContext.Provider value={autoConversionContextValue}>
			{children}
			{!isFabric && queryDataId && checkRevertFlag(queryDataId) && (
				<div data-testid="auto-editor-conversion-revert-success">
					<RevertEditorSuccess />
				</div>
			)}
		</AutoConversionByLineContext.Provider>
	);
};

export const useAutoConversionByLineContext = (): AutoConversionContextValue =>
	useContext(AutoConversionByLineContext);
